import { useMemo } from "react"
import styled from "styled-components"
import { Button } from "../Button"
import { Modal } from "../Modal"
import { overloadColor } from "../util/style"
import type {
  PromptButtonStyle,
  PromptResolve,
  PromptState,
  PromptStyleOptions,
  PromptType,
} from "./types"

interface WrapperProps extends PromptStyleOptions {}

const Wrapper = styled.div<WrapperProps>`
  background: ${(props) => overloadColor(props.background)};
  padding: ${(props) => props.padding};
  border-radius: ${(props) => props.borderRadius};

  div.buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;

    text-transform: uppercase;
    font-size: 0.8rem;

    & > * + * {
      margin-left: 1rem;
    }
  }
`
interface PromptButtonProps extends PromptButtonStyle {}
const PromptButton = styled(Button)<PromptButtonProps>`
  padding: ${(props) => props.padding};

  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};

  cursor: ${(props) => props.cursor};

  &:hover {
    color: ${(props) => overloadColor(props.hoverColor)};
    background: ${(props) => overloadColor(props.hoverBackground)};
  }
`

type NormalPromptType<T> = Omit<PromptType<T>, "content"> & { content: string }

interface NormalPromptProps<T> {
  prompt: NormalPromptType<T>
  onResolve: PromptResolve<T>
}
const NormalPrompt = <T,>({ prompt, onResolve }: NormalPromptProps<T>) => {
  const { buttons, style } = prompt.options

  return (
    <Wrapper {...style}>
      {prompt.content}

      <div className="buttons">
        {buttons.map((button) => (
          <PromptButton
            key={button.label}
            {...button.style}
            onClick={() => onResolve(button.value as unknown as T)}
          >
            {button.label}
          </PromptButton>
        ))}
      </div>
    </Wrapper>
  )
}

interface PromptProps<T, S extends PromptState = PromptState> {
  prompt: PromptType<T, S>
}
export const Prompt = <T, S extends PromptState = PromptState>({
  prompt,
}: PromptProps<T, S>) => {
  const content = useMemo(() => {
    if (typeof prompt.content === "string")
      return (
        <NormalPrompt
          prompt={prompt as NormalPromptType<T>}
          onResolve={(prompt as NormalPromptType<T>).resolve}
        />
      )

    return prompt.content(prompt.resolve, prompt.reject, { id: prompt.id })
  }, [prompt])

  const { closeOnClickOutside, closeOnEscape, ...modalProps } =
    prompt.options.modalProps

  return (
    <Modal
      {...modalProps}
      closeOnEscape={closeOnEscape}
      open={prompt.open}
      onShadeClick={
        closeOnClickOutside ? () => prompt.reject(-1) : () => void 0
      }
      onClose={
        closeOnEscape
          ? (evt) =>
              !!evt && "key" in evt && evt.key === "Escape" && prompt.reject(-1)
          : () => void 0
      }
    >
      {content}
    </Modal>
  )
}
